import React, { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Row,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  ButtonGroup,
  Button,
} from "reactstrap";
import ReactApexChart from "react-apexcharts";
import DashboardService from "../../../../services/OrderingServices/DashboardService";

const Revenue = () => {
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [viewMode, setViewMode] = useState("weekly");
  const [availableYears] = useState(
    Array.from({ length: 6 }, (_, i) => currentYear - i)
  );
  const [allYearsData, setAllYearsData] = useState({});
  const [summaryStats, setSummaryStats] = useState({
    earnings: 0,
    avgDaily: 0,
    growth: 0,
    peak: 0,
  });
  const [loading, setLoading] = useState(false);
  const [isYearDropdownOpen, setIsYearDropdownOpen] = useState(false);
  const [chartSeries, setChartSeries] = useState([]);

  // ====================================================
  // 🔥 FIX: Fill Missing Daily Dates
  // ====================================================
  const fillMissingDailyDates = (yearData = []) => {
    if (!Array.isArray(yearData) || yearData.length === 0) return [];

    const filled = [];

    yearData.forEach((item) => {
      if (!item || !item.year || !item.month) return;

      const dailySales = Array.isArray(item.dailySales) ? item.dailySales : [];
      const daysInMonth = new Date(item.year, item.month, 0).getDate();

      const dailyMap = {};
      dailySales.forEach((d) => {
        if (d?.day) dailyMap[d.day] = d.totalSales || 0;
      });

      const completedDaily = [];
      for (let day = 1; day <= daysInMonth; day++) {
        completedDaily.push({
          day,
          totalSales: dailyMap[day] ?? 0,
        });
      }

      filled.push({
        year: item.year,
        month: item.month,
        totalSales: completedDaily.reduce((s, d) => s + d.totalSales, 0),
        dailySales: completedDaily,
      });
    });

    return filled;
  };

  // ====================================================
  // 🔥 FIX: Monthly - Always show Jan to Current Month
  // ====================================================
  const transformToMonthly = (yearData) => {
    const monthMap = {};
    const today = new Date();
    const currentMonth = today.getMonth() + 1; // 1-12
    const isCurrentYear = selectedYear === today.getFullYear();

    // Fill months from Jan to current month (or all 12 if past year)
    // const maxMonth = isCurrentYear ? currentMonth : 12;
    const maxMonth = 12;

    for (let m = 1; m <= maxMonth; m++) {
      monthMap[m] = 0;
    }

    // Replace with real data
    yearData.forEach((item) => {
      if (item.month <= maxMonth) {
        monthMap[item.month] = item.totalSales;
      }
    });

    // Convert for chart
    return Object.entries(monthMap).map(([month, total]) => {
      const date = Date.UTC(selectedYear, parseInt(month) - 1, 1);
      return [date, total];
    });
  };

  // ====================================================
  // 🔥 FIX: Weekly - Show Last 4 Weeks with Week Labels
  // ====================================================
  const transformToWeekly = (yearData = []) => {
    const allDaily = yearData.flatMap((m) =>
      (m.dailySales || []).map((d) => ({
        year: m.year,
        month: m.month,
        day: d.day,
        totalSales: d.totalSales || 0,
      }))
    );

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // 🔥 find last Sunday
    const lastSunday = new Date(today);
    lastSunday.setDate(today.getDate() - today.getDay()); // Sunday = 0

    const series = [];

    // 🔥 last 4 weeks (Sunday based)
    for (let i = 3; i >= 0; i--) {
      const weekStart = new Date(lastSunday);
      weekStart.setDate(lastSunday.getDate() - i * 7);

      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);

      let weekTotal = 0;

      for (let d = 0; d < 7; d++) {
        const date = new Date(weekStart);
        date.setDate(weekStart.getDate() + d);

        const dayData = allDaily.find(
          (x) =>
            x.year === date.getFullYear() &&
            x.month === date.getMonth() + 1 &&
            x.day === date.getDate()
        );

        weekTotal += dayData?.totalSales || 0;
      }

      // ✅ X-axis = Sunday ONLY
      series.push([
        Date.UTC(
          weekStart.getFullYear(),
          weekStart.getMonth(),
          weekStart.getDate()
        ),
        weekTotal,
      ]);
    }

    return series;
  };

  // ====================================================
  // 🔥 FIX: Daily - Show Current Month from Day 1 to Today
  // ====================================================
  const transformToDaily = (yearData) => {
    const series = [];
    const today = new Date();
    const currentMonth = today.getMonth() + 1;
    const currentYear = today.getFullYear();
    const currentDay = today.getDate();

    // Filter to show only current month data
    const currentMonthData = yearData.find(
      (item) => item.year === currentYear && item.month === currentMonth
    );

    if (!currentMonthData) {
      return [];
    }

    // Show from day 1 to today
    for (let day = 1; day <= currentDay; day++) {
      const dayData = currentMonthData.dailySales.find((d) => d.day === day);
      const timestamp = Date.UTC(currentYear, currentMonth - 1, day);
      series.push([timestamp, dayData?.totalSales || 0]);
    }

    return series;
  };

  // ====================================================
  // Summary Stats
  // ====================================================
  const calculateStats = (yearData = []) => {
    if (!Array.isArray(yearData) || yearData.length === 0) {
      return {
        earnings: 0,
        avgDaily: 0,
        growth: 0,
        peak: 0,
      };
    }

    const allMonthly = yearData.filter(
      (m) => m && typeof m.totalSales === "number"
    );

    if (allMonthly.length === 0) {
      return {
        earnings: 0,
        avgDaily: 0,
        growth: 0,
        peak: 0,
      };
    }

    const totalSales = allMonthly.reduce((sum, m) => sum + m.totalSales, 0);
    const allDaily = allMonthly.flatMap((m) => m.dailySales || []);

    const daysWithSales = allDaily.length;
    const avgDaily = daysWithSales ? totalSales / daysWithSales : 0;
    const peak = Math.max(...allDaily.map((d) => d.totalSales || 0), 0);

    const sorted = [...allMonthly].sort((a, b) => a.month - b.month);
    const first = sorted[0].totalSales;
    const last = sorted[sorted.length - 1].totalSales;
    const growth = first > 0 ? (((last - first) / first) * 100).toFixed(1) : 0;

    return {
      earnings: totalSales / 1000,
      avgDaily: avgDaily / 1000,
      growth,
      peak: peak / 1000,
    };
  };

  // ====================================================
  // Fetch Data
  // ====================================================
  const fetchDataForYear = async (year) => {
    try {
      setLoading(true);
      const res = await DashboardService.getReport(year);
      setLoading(false);

      const cleaned = fillMissingDailyDates(res || []);
      return cleaned;
    } catch (e) {
      console.error("Error", e);
      setLoading(false);
      return [];
    }
  };

  // ====================================================
  // Initialize
  // ====================================================
  const fetchInitialData = async () => {
    const data = await fetchDataForYear(selectedYear);
    setAllYearsData({ [selectedYear]: data });
    updateChartAndSummary(data);
  };

  useEffect(() => {
    fetchInitialData();
  }, []);

  // ====================================================
  // Update Chart + Summary
  // ====================================================
  const updateChartAndSummary = (yearData = []) => {
    if (!Array.isArray(yearData) || yearData.length === 0) {
      setChartSeries([{ name: "Sales", data: [] }]);
      setSummaryStats(calculateStats([]));
      return;
    }

    let series = [];

    if (viewMode === "daily") {
      series = transformToDaily(yearData);
    } else if (viewMode === "weekly") {
      series = transformToWeekly(yearData);
    } else {
      series = transformToMonthly(yearData);
    }

    setChartSeries([{ name: "Sales", data: series }]);
    setSummaryStats(calculateStats(yearData));
  };

  // When viewMode changes
  useEffect(() => {
    const data = allYearsData[selectedYear];
    if (data) updateChartAndSummary(data);
  }, [viewMode]);

  const handleYearChange = async (year) => {
    setSelectedYear(year);

    let data = allYearsData[year];
    if (!data) {
      data = await fetchDataForYear(year);
      setAllYearsData((prev) => ({ ...prev, [year]: data }));
    }
    updateChartAndSummary(data);
  };

  // ====================================================
  // 🔥 FIX: Chart Options - Different formats per view
  // ====================================================
  const chartOptions = {
    chart: {
      type: "area",
      height: 350,
      zoom: { enabled: true, autoScaleYaxis: true },
      toolbar: {
        show: true,
        tools: {
          zoom: true,
          zoomin: true,
          zoomout: true,
          pan: true,
          reset: true,
        },
      },
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      curve: "smooth",
      width: 2,
    },
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.4,
        opacityTo: 0.1,
      },
    },
    xaxis: {
      type: "datetime",
      labels: {
        format:
          viewMode === "monthly"
            ? "MMM"
            : viewMode === "weekly"
            ? "MMM dd"
            : "dd MMM",
        rotate: -45,
        rotateAlways: false,
        hideOverlappingLabels: true,
        datetimeFormatter: {
          year: "yyyy",
          month: "MMM",
          day: "dd MMM",
          hour: "HH:mm",
        },
      },
      tickAmount:
        viewMode === "daily" ? 15 : viewMode === "weekly" ? 7 : undefined,
    },
    yaxis: {
      labels: {
        formatter: (val) =>
          val >= 1000 ? (val / 1000).toFixed(1) + "k" : val.toFixed(0),
      },
    },
    tooltip: {
      x: {
        format: viewMode === "monthly" ? "MMM yyyy" : "ddd, dd MMM yyyy",
      },
      y: {
        formatter: (val) => {
          const formatted =
            val >= 1000 ? (val / 1000).toFixed(1) + "k" : val.toFixed(0);
          return `Rs. ${formatted}`;
        },
      },
    },
    grid: {
      borderColor: "#f1f1f1",
      padding: {
        left: 10,
        right: 10,
      },
    },
    colors: ["#f79400"], // 🔥 Blue color for all views
  };

  // ====================================================
  // UI
  // ====================================================
  return (
    <Card className="h-100">
      <CardHeader className="">
        <Row>
          <Col md={4} xs={12}>
            <h4 className="mb-2">Revenue</h4>
          </Col>
          <Col md={8} className="">
            {" "}
            <div className="d-flex gap-2 align-items-center w-100 justify-content-end ">
              <ButtonGroup className="bg-light p-1 rounded-pill">
                {["daily", "weekly", "monthly"].map((mode) => {
                  const isActive = viewMode === mode;

                  return (
                    <Button
                      key={mode}
                      size="sm"
                      onClick={() => setViewMode(mode)}
                      className={`rounded-pill px-3 fw-medium ${
                        isActive
                          ? "shadow-sm"
                          : "btn-outline-secondary border-0"
                      }`}
                      style={{
                        backgroundColor: isActive ? "#f79400" : "transparent",
                        borderColor: "#f79400",
                        color: isActive ? "#000" : "#6c757d",
                        opacity: isActive ? 1 : 0.85,
                        textTransform: "capitalize",
                        // transition: "all 0.2s ease",
                        fontWeight: isActive ? 600 : 500,
                      }}
                      onMouseEnter={(e) => {
                        if (!isActive) {
                          e.currentTarget.style.backgroundColor = "#f79400";
                          e.currentTarget.style.color = "#fff";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isActive) {
                          e.currentTarget.style.backgroundColor = "transparent";
                          e.currentTarget.style.color = "#6c757d";
                        }
                      }}
                    >
                      {mode}
                    </Button>
                  );
                })}
              </ButtonGroup>

              <Dropdown
                isOpen={isYearDropdownOpen}
                toggle={() => setIsYearDropdownOpen(!isYearDropdownOpen)}
              >
                <DropdownToggle
                  className="btn btn-sm"
                  caret
                  disabled={loading}
                  style={{
                    backgroundColor: "#f79400",
                    borderColor: "#f79400",
                    color: "#000",
                    fontWeight: 500,
                    transition: "all 0.2s ease-in-out",
                  }}
                >
                  {loading ? "Loading..." : selectedYear}
                </DropdownToggle>

                <DropdownMenu>
                  {availableYears.map((year) => (
                    <DropdownItem
                      key={year}
                      active={selectedYear === year}
                      onClick={() => handleYearChange(year)}
                    >
                      {year}
                    </DropdownItem>
                  ))}
                </DropdownMenu>
              </Dropdown>
            </div>
          </Col>
        </Row>
      </CardHeader>

      {/* Summary Section */}
      <CardHeader className="p-2 bg-light">
        <Row className="text-center g-0">
          <Col xs={6} sm={3}>
            <h5>{summaryStats.earnings.toFixed(1)}k</h5>
            <p className="small text-muted">Total Earnings</p>
          </Col>

          <Col xs={6} sm={3}>
            <h5>{summaryStats.avgDaily.toFixed(1)}k</h5>
            <p className="small text-muted">Avg Daily</p>
          </Col>

          <Col xs={6} sm={3}>
            <h5 style={{ color: summaryStats.growth >= 0 ? "green" : "red" }}>
              {summaryStats.growth}%
            </h5>
            <p className="small text-muted">Monthly Growth</p>
          </Col>

          <Col xs={6} sm={3}>
            <h5>{summaryStats.peak.toFixed(1)}k</h5>
            <p className="small text-muted">Peak Day</p>
          </Col>
        </Row>
      </CardHeader>

      {/* Chart */}
      <CardBody>
        {loading ? (
          <div className="text-center">Loading...</div>
        ) : (
          <ReactApexChart
            options={chartOptions}
            series={chartSeries}
            type="area"
            height={370}
          />
        )}
      </CardBody>
    </Card>
  );
};

export default Revenue;
